import { IconProp } from '@fortawesome/fontawesome-svg-core';
import PubSub from 'pubsub-js';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { subscribe, SubscriptionTopic } from '../../viewer/messaging/pubsub';
import { Button } from '../components/button/button';
import { Icon } from '../components/icon/icon';
import { Stack } from '../components/stack/stack';
import type { ShowToastOptions, Toast, ToastrMessage, ToastType } from './toaster-types';

type ToasterContextValue = {
  showToast: (options: ShowToastOptions) => string;
  dismissToast: (id: string) => void;
};

const ToasterContext = React.createContext<ToasterContextValue | undefined>(undefined);

const DEFAULT_DURATION = 7000;

const toastVisuals: Record<
  ToastType,
  { accent: string; background: string; icon: IconProp; aria: 'polite' | 'assertive' }
> = {
  success: {
    accent: '#00ad83',
    background: '#f0f9f5',
    icon: ['fas', 'circle-check'],
    aria: 'polite',
  },
  info: {
    accent: '#00a9e0',
    background: '#edf8ff',
    icon: ['fas', 'circle-info'],
    aria: 'polite',
  },
  warning: {
    accent: '#ffb547',
    background: '#fff7e8',
    icon: ['fas', 'triangle-exclamation'],
    aria: 'assertive',
  },
  error: {
    accent: '#db4d69',
    background: '#fff1f4',
    icon: ['fas', 'circle-exclamation'],
    aria: 'assertive',
  },
};

const toasterTypeMap: Record<string, ToastType> = {
  success: 'success',
  error: 'error',
  warn: 'warning',
  warning: 'warning',
  info: 'info',
};

const DEFAULT_DISMISSIBLE = true;

let toastrMessageOwner: symbol | undefined;

export const ToasterState = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timers = React.useRef<Map<string, number>>(new Map());
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

  const dismissToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const showToast = React.useCallback(
    (options: ShowToastOptions) => {
      const id = options.id ?? uuid();
      const existingTimer = timers.current.get(id);
      // If the toast already has a timer, clear it.
      if (existingTimer) {
        clearTimeout(existingTimer);
        timers.current.delete(id);
      }

      setToasts((prev) => {
        const toast: Toast = {
          dismissible: DEFAULT_DISMISSIBLE,
          duration: DEFAULT_DURATION,
          ...options,
          id,
        };

        return [...prev.filter((item) => item.id !== id), toast];
      });

      const duration = options.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        const timer = window.setTimeout(() => {
          dismissToast(id);
        }, duration);
        timers.current.set(id, timer);
      }

      return id;
    },
    [dismissToast],
  );

  React.useEffect(() => {
    const timersMap = timers.current;
    return () => {
      for (const timer of timersMap.values()) {
        clearTimeout(timer);
      }
      timersMap.clear();
    };
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const container = document.createElement('div');
    container.setAttribute('data-toaster-portal', 'true');
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      document.body.removeChild(container);
      setPortalContainer(null);
    };
  }, []);

  const getDefaultTitle = React.useCallback(
    (type: ToastType) => {
      switch (type) {
        case 'success':
          return t('toaster.title.success', { ns: 'common' });
        case 'info':
          return t('toaster.title.info', { ns: 'common' });
        case 'warning':
          return t('toaster.title.warning', { ns: 'common' });
        case 'error':
        default:
          return t('toaster.title.error', { ns: 'common' });
      }
    },
    [t],
  );

  React.useEffect(() => {
    const instanceId = Symbol('toastr-subscription');
    if (!toastrMessageOwner) {
      toastrMessageOwner = instanceId;
    }

    if (toastrMessageOwner !== instanceId) {
      return;
    }

    const toastrToken = subscribe(SubscriptionTopic.ToastrMessage, (message: ToastrMessage) => {
      const type = toasterTypeMap[message.type] ?? 'info';
      showToast({
        title: message.title ?? getDefaultTitle(type),
        message: message.message,
        type,
        duration: message.duration,
        dismissible: message.dismissible,
        id: message.id,
      });
    });

    return () => {
      if (toastrMessageOwner === instanceId) {
        if (typeof toastrToken === 'string') {
          PubSub.unsubscribe(toastrToken);
        }
        toastrMessageOwner = undefined;
      }
    };
  }, [showToast, getDefaultTitle]);

  return (
    <ToasterContext.Provider
      value={React.useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast])}
    >
      {children}
      {portalContainer
        ? createPortal(
            <ToastViewport role="presentation">
              {toasts.map((toast) => {
                const visuals = toastVisuals[toast.type];
                return (
                  <ToastCard
                    $accentColor={visuals.accent}
                    $background={visuals.background}
                    aria-live={visuals.aria}
                    direction="row"
                    key={toast.id}
                    role="status"
                    spacing={0.75}
                  >
                    <Icon color={visuals.accent} icon={visuals.icon} />
                    <ToastContent spacing={0.5}>
                      <ToastHeader
                        alignItems="flex-start"
                        direction="row"
                        justifyContent="space-between"
                      >
                        <ToastTitle>{toast.title}</ToastTitle>
                        {toast.dismissible !== false && (
                          <DismissButton
                            aria-label={t('close', { ns: 'common' })}
                            onClick={() => dismissToast(toast.id)}
                          >
                            <Icon icon="times" />
                          </DismissButton>
                        )}
                      </ToastHeader>
                      <ToastMessage>{toast.message}</ToastMessage>
                      {toast.action && (
                        <ToastAction
                          contentAlignment="left"
                          variant="text"
                          onClick={() => {
                            const result = toast.action?.onClick();
                            if (result && typeof (result as Promise<unknown>).then === 'function') {
                              void (result as Promise<unknown>).finally(() =>
                                dismissToast(toast.id),
                              );
                              return;
                            }
                            dismissToast(toast.id);
                          }}
                        >
                          {toast.action.label}
                        </ToastAction>
                      )}
                    </ToastContent>
                  </ToastCard>
                );
              })}
            </ToastViewport>,
            portalContainer,
          )
        : null}
    </ToasterContext.Provider>
  );
};

export const useToaster = () => {
  const context = React.useContext(ToasterContext);
  if (!context) {
    throw new Error('useToaster must be used within a ToasterState provider');
  }
  return context;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ToastViewport = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  /* Make sure this is higher than Intercom launcher frame, and everything else in the app. */
  z-index: ${Number.MAX_SAFE_INTEGER};
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
`;

const ToastCard = styled(Stack)<{ $accentColor: string; $background: string }>`
  min-width: 280px;
  max-width: 400px;
  background: ${(props) => props.$background};
  border-left: 4px solid ${(props) => props.$accentColor};
  border-radius: 8px;
  padding: 16px;
  box-shadow:
    0 4px 12px rgba(37, 38, 94, 0.12),
    0 2px 6px rgba(37, 38, 94, 0.08);
  color: ${(props) => props.theme.color.gray.darkest};
  pointer-events: auto;
  animation: ${fadeIn} 150ms ease-out;
`;

const ToastContent = styled(Stack)`
  flex: 1;
`;

const ToastHeader = styled(Stack)`
  gap: 16px;
`;

const ToastTitle = styled.span`
  font-weight: 600;
  font-size: 0.95rem;
`;

const ToastMessage = styled.span`
  font-size: 0.9rem;
  line-height: 1.4;
  color: ${(props) => props.theme.color.gray.dark};
  word-break: break-word;
`;

const DismissButton = styled.button`
  ${(props) => css`
    border: none;
    background: transparent;
    color: ${props.theme.color.gray.darkest};
    cursor: pointer;
    padding: 0;
    display: flex;
  `}
`;

const ToastAction = styled(Button)`
  align-self: flex-start;
  padding: 0;
  font-size: 0.9rem;
`;
